import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import api from '@/lib/api';

/**
 * Reset password landing page — reached via the emailed link.
 * Verifies the token exists client-side by requiring it in the query string,
 * then POSTs {token, new_password} to /auth/reset-password.
 */
export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) setError('This link is missing its token. Request a new reset email.');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 8) return setError('Password must be at least 8 characters');
    if (password !== confirm) return setError('Passwords do not match');
    setBusy(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not reset password');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="container-x py-24 max-w-md">
        <span className="eyebrow">Trade Partners</span>
        <h1 className="h-display text-3xl md:text-4xl mt-4">Password updated.</h1>
        <p className="sans text-sm text-neutral-500 mt-3">You&rsquo;ll be redirected to sign in shortly.</p>
        <Link to="/login" className="btn-primary mt-8 inline-flex" data-testid="reset-done-signin">Sign in now</Link>
      </div>
    );
  }

  return (
    <div className="container-x py-24 max-w-md">
      <span className="eyebrow">Trade Partners</span>
      <h1 className="h-display text-3xl md:text-4xl mt-4">Reset password.</h1>
      <p className="sans text-sm text-neutral-500 mt-3">Set a new password for your IJE HAUS account.</p>

      <form onSubmit={submit} className="mt-10 space-y-6" data-testid="reset-password-form">
        <div>
          <label className="ije-label">New password</label>
          <input
            required
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="ije-input"
            minLength={8}
            data-testid="reset-password-input"
          />
        </div>
        <div>
          <label className="ije-label">Confirm password</label>
          <input
            required
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="ije-input"
            minLength={8}
            data-testid="reset-password-confirm"
          />
        </div>
        {error && (
          <div className="sans text-sm" style={{ color: 'var(--ije-pink-dark)' }} data-testid="reset-password-error">{error}</div>
        )}
        <button type="submit" disabled={busy || !token} className="btn-primary w-full" data-testid="reset-password-submit">
          {busy ? <Loader2 size={14} className="animate-spin" /> : null} {busy ? 'Saving…' : 'Set new password'}
        </button>
        <p className="sans text-xs text-neutral-500 text-center">
          <Link to="/login" className="link-pink">Back to sign in</Link>
        </p>
      </form>
    </div>
  );
}
